import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errorText } from "../api";
import { useBusiness } from "../business";
import { ConfirmDialog, Dialog } from "../dialog";
import { Banner, toast } from "../editor";
import { Field, NumberField, TextField } from "../fields";
import { CopyRow } from "../steps";
import { Badge, Card, Empty, Icon, Menu, Pager, Tabs, fmtDateTime, plural } from "../ui";

/**
 * Settings › Payments: the business's own Stripe account, connected to Mivio, takes payments for paid events and
 * the shop. Stripe pays the business out directly; Mivio never holds the money. Mivio HQ also connects Mivio's own
 * Stripe account here, once, for everyone.
 */

interface Account {
  accountId: string;
  country: string;
  businessName: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsDue: string[];
  disabledReason: string | null;
  checkedAt: string;
  ready: boolean;
}
interface Status { platform: { ready: boolean; livemode: boolean }; account: Account | null; currency: string; last30: { payments: number; paid: number; refunded: number } }
interface PaymentRow {
  id: string;
  reference: string;
  description: string;
  amount: number;
  currency: string;
  status: "open" | "paid" | "expired" | "failed" | "refunded" | "partly_refunded";
  amountRefunded: number;
  paidAt: string | null;
  createdAt: string;
  solution: string;
  email: string | null;
  member: { id: string; name: string; email: string } | null;
}
interface Platform {
  connected: boolean;
  accountId: string | null;
  livemode: boolean;
  name: string | null;
  feePercent: number;
  hint: string | null;
  verifiedAt: string | null;
  webhookUrl: string;
  webhookEvents: string[];
  connectedBusinesses: number;
}

const STATUS: Record<PaymentRow["status"], { label: string; tone: "neutral" | "success" | "info" | "warn" | "accent" | "error" }> = {
  open: { label: "Waiting for payment", tone: "info" },
  paid: { label: "Paid", tone: "success" },
  expired: { label: "Not paid", tone: "neutral" },
  failed: { label: "Failed", tone: "error" },
  refunded: { label: "Refunded", tone: "warn" },
  partly_refunded: { label: "Partly refunded", tone: "warn" },
};

function useMoney() {
  const { data } = useBusiness();
  return (cents: number, currency = data.business.currency) => new Intl.NumberFormat(data.business.locale || "en-AU", { style: "currency", currency }).format(cents / 100);
}

export function PaymentsSettings() {
  const { data } = useBusiness();
  const hq = data.business.kind === "hq" && data.me.owner;
  return (
    <>
      {hq && <PlatformCard />}
      <StripeCard />
      <PaymentsList />
    </>
  );
}

function StripeCard() {
  const qc = useQueryClient();
  const money = useMoney();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ["payments", "status"], queryFn: () => api<Status>("/payments") });
  const [busy, setBusy] = useState(false);
  const [off, setOff] = useState(false);
  const handled = useRef(false);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api<{ url: string }>("/payments/connect", { method: "POST" });
      window.location.assign(r.url);
    } catch (err) {
      toast(errorText(err), "error");
      setBusy(false);
    }
  };
  const refresh = async (quiet = false) => {
    setBusy(true);
    try {
      qc.setQueryData(["payments", "status"], await api<Status>("/payments/refresh", { method: "POST" }));
      if (!quiet) toast("Checked with Stripe");
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(false);
    }
  };

  // Back from Stripe's form: see how it stands. Stripe's link ran out: start a fresh one.
  useEffect(() => {
    const from = params.get("stripe");
    if (!from || handled.current) return;
    handled.current = true;
    setParams((p) => {
      p.delete("stripe");
      return p;
    }, { replace: true });
    if (from === "back") void refresh(true);
    if (from === "again") void connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  if (q.isPending) return <Card title="Stripe"><div className="loading">Loading…</div></Card>;
  if (q.isError || !q.data) return <Banner tone="critical" title="Payments didn't load">{errorText(q.error)}</Banner>;
  const s = q.data;
  const a = s.account;
  return (
    <>
      {!s.platform.ready && (
        <Banner tone="info" title="Online payments aren't open yet">
          Mivio switches them on for everyone once its own Stripe account is connected. Nothing for you to do yet.
        </Banner>
      )}
      <Card
        title="Stripe"
        action={
          <span className="card__tools">
            {a ? (a.ready ? <Badge tone="success">Taking payments</Badge> : <Badge tone="warn">Setup not finished</Badge>) : <Badge tone="neutral">Not connected</Badge>}
            {s.platform.ready && !s.platform.livemode && <Badge tone="warn">Test mode</Badge>}
            {a && (
              <a className="btn btn--sm" href="https://dashboard.stripe.com/" target="_blank" rel="noreferrer">
                Open Stripe <Icon.external />
              </a>
            )}
          </span>
        }
        foot="Your own Stripe account takes the payments and pays them into your bank. Mivio starts each payment and hears when it's paid; it never holds your money or sees card details."
      >
        {!a ? (
          <>
            <p className="acct-plain">Connect Stripe to take payments for paid events and the shop. Stripe asks for your business and bank details in its own secure form, then sends you back here.</p>
            <button type="button" className="btn btn--primary" disabled={busy || !s.platform.ready} onClick={() => void connect()}>
              {busy ? "Opening Stripe…" : "Connect Stripe"}
            </button>
          </>
        ) : (
          <>
            <ul className="twostep-list">
              <li>
                <Icon.doc />
                <span>
                  <strong>Business details</strong>
                  <small>{a.detailsSubmitted ? "Sent to Stripe" : "Stripe still needs them"}</small>
                </span>
                {a.detailsSubmitted ? <Badge tone="success">Done</Badge> : <Badge tone="warn">To do</Badge>}
              </li>
              <li>
                <Icon.card />
                <span>
                  <strong>Taking payments</strong>
                  <small>{a.chargesEnabled ? "Members can pay online" : "Off until Stripe has checked your details"}</small>
                </span>
                {a.chargesEnabled ? <Badge tone="success">On</Badge> : <Badge tone="warn">Off</Badge>}
              </li>
              <li>
                <Icon.bank />
                <span>
                  <strong>Payouts to your bank</strong>
                  <small>{a.payoutsEnabled ? "Stripe pays you out on its usual schedule" : "Stripe needs your bank account"}</small>
                </span>
                {a.payoutsEnabled ? <Badge tone="success">On</Badge> : <Badge tone="warn">Off</Badge>}
              </li>
            </ul>
            {a.requirementsDue.length > 0 && (
              <Banner tone="warning" title={`Stripe needs ${plural(a.requirementsDue.length, "more detail")}`}>
                Carry on in Stripe's form to finish. Until then {a.chargesEnabled ? "payouts may pause" : "you can't take payments"}.
              </Banner>
            )}
            <dl className="dl payments-dl">
              <dt>Stripe account</dt>
              <dd className="mono">{a.accountId}</dd>
              <dt>Last checked</dt>
              <dd>{fmtDateTime(a.checkedAt)}</dd>
              {a.ready && (
                <>
                  <dt>Last 30 days</dt>
                  <dd>
                    {plural(s.last30.payments, "payment")} · {money(s.last30.paid)}
                    {s.last30.refunded ? ` · ${money(s.last30.refunded)} refunded` : ""}
                  </dd>
                </>
              )}
            </dl>
            <div className="twostep-row payments-actions">
              {!a.ready || a.requirementsDue.length > 0 ? (
                <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void connect()}>
                  Continue in Stripe
                </button>
              ) : null}
              <button type="button" className="btn" disabled={busy} onClick={() => void refresh()}>
                Check again
              </button>
              <button type="button" className="btn btn--plain btn--danger-ghost" disabled={busy} onClick={() => setOff(true)}>
                Disconnect
              </button>
            </div>
          </>
        )}
      </Card>
      <ConfirmDialog
        open={off}
        title="Disconnect Stripe?"
        text="Online payments stop straight away: paid events and the shop can't take money until you connect again. Your Stripe account, its payments and payouts stay as they are."
        confirmLabel="Disconnect"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          setBusy(true);
          void api("/payments/connect", { method: "DELETE" })
            .then(() => {
              toast("Stripe disconnected");
              void qc.invalidateQueries({ queryKey: ["payments"] });
            }, (err) => toast(errorText(err), "error"))
            .finally(() => {
              setBusy(false);
              setOff(false);
            });
        }}
        onCancel={() => setOff(false)}
      />
    </>
  );
}

function PaymentsList() {
  const money = useMoney();
  const qc = useQueryClient();
  const [view, setView] = useState<"all" | "paid" | "refunded" | "open">("all");
  const [page, setPage] = useState(1);
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const q = useQuery({
    queryKey: ["payments", "list", view, page],
    queryFn: () => api<{ payments: PaymentRow[]; pagination: { page: number; totalPages: number; total: number } }>(`/payments/list?status=${view}&page=${page}`),
    placeholderData: (p) => p,
  });
  const rows = q.data?.payments ?? [];
  return (
    <Card title="Payments" flush>
      <div className="card__tabs">
        <Tabs
          tabs={[
            { key: "all", label: "All" },
            { key: "paid", label: "Paid" },
            { key: "refunded", label: "Refunded" },
            { key: "open", label: "Waiting" },
          ]}
          value={view}
          onChange={(k) => {
            setView(k);
            setPage(1);
          }}
        />
      </div>
      {q.isPending ? (
        <div className="loading">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty title="No payments yet" text="Payments for paid events and the shop show here as they come in." />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Payment</th>
                <th className="hide-sm">Date</th>
                <th>Status</th>
                <th className="num">Amount</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="cell__text">
                      <strong>{p.description}</strong>
                      <span>{p.member?.name || p.member?.email || p.email || "Guest"}</span>
                    </div>
                  </td>
                  <td className="hide-sm">{fmtDateTime(p.paidAt ?? p.createdAt)}</td>
                  <td>
                    <Badge tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Badge>
                  </td>
                  <td className="num">
                    {money(p.amount, p.currency)}
                    {p.amountRefunded > 0 && <span className="muted payments-refunded">−{money(p.amountRefunded, p.currency)}</span>}
                  </td>
                  <td className="num cell--menu">
                    {(p.status === "paid" || p.status === "partly_refunded") && (
                      <Menu compact label="Actions" icon={<Icon.more />} ariaLabel={`Actions for ${p.description}`} items={[{ key: "refund", label: "Refund…", onSelect: () => setRefunding(p) }]} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data && <Pager page={q.data.pagination.page} totalPages={q.data.pagination.totalPages} total={q.data.pagination.total} onPage={setPage} />}
        </>
      )}
      {refunding && (
        <RefundDialog
          payment={refunding}
          onClose={() => setRefunding(null)}
          onDone={() => {
            setRefunding(null);
            void qc.invalidateQueries({ queryKey: ["payments"] });
          }}
        />
      )}
    </Card>
  );
}

function RefundDialog({ payment, onClose, onDone }: { payment: PaymentRow; onClose: () => void; onDone: () => void }) {
  const money = useMoney();
  const left = payment.amount - payment.amountRefunded;
  const [amount, setAmount] = useState((left / 100).toFixed(2));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = Math.round(Number(amount) * 100);
  const bad = !(cents > 0 && cents <= left);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (bad) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/payments/${payment.id}/refund`, { method: "POST", json: { amount: cents, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
      toast(`Refunded ${money(cents, payment.currency)}`);
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Dialog title="Refund payment" onClose={onClose} busy={busy}>
      <form onSubmit={(e) => void submit(e)}>
        <p>
          {payment.description}: {money(payment.amount, payment.currency)} paid{payment.amountRefunded ? `, ${money(payment.amountRefunded, payment.currency)} already refunded` : ""}. Stripe sends it back to the card used; it takes 5 to 10 days to show.
        </p>
        {error && <Banner tone="critical" title="Not refunded">{error}</Banner>}
        <Field label={`Amount (${payment.currency})`} error={amount && bad ? `Refund up to ${money(left, payment.currency)}.` : undefined}>
          {(id, d) => <input id={id} aria-describedby={d} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />}
        </Field>
        <TextField label="Reason (optional)" value={reason} onChange={setReason} maxLength={300} help="Kept on the Timeline, for your team." />
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--danger" disabled={busy || bad}>{busy ? "Refunding…" : `Refund ${bad ? "" : money(cents, payment.currency)}`}</button>
        </div>
      </form>
    </Dialog>
  );
}

/* ---------- Mivio HQ: Mivio's own Stripe account ---------- */

function PlatformCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["payments", "platform"], queryFn: () => api<Platform>("/payments/platform") });
  const [editing, setEditing] = useState(false);
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [fee, setFee] = useState<number | string>(0);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [off, setOff] = useState(false);
  const p = q.data;
  useEffect(() => {
    if (p) setFee(p.feePercent);
  }, [p]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setProblem(null);
    try {
      qc.setQueryData(["payments", "platform"], await api<Platform>("/payments/platform", { method: "PUT", json: { secretKey, webhookSecret, feePercent: Number(fee) || 0 } }));
      setSecretKey("");
      setWebhookSecret("");
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["payments", "status"] });
      toast("Mivio's Stripe account connected");
    } catch (err) {
      const fields = (err as { errors?: Record<string, string> }).errors;
      if (fields) setErrors(fields);
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const saveFee = async () => {
    setBusy(true);
    try {
      qc.setQueryData(["payments", "platform"], await api<Platform>("/payments/platform", { method: "PATCH", json: { feePercent: Number(fee) || 0 } }));
      toast("Fee saved");
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setBusy(false);
    }
  };

  if (q.isPending || !p) return <Card title="Mivio's Stripe account"><div className="loading">Loading…</div></Card>;
  return (
    <>
      <Card
        title="Mivio's Stripe account"
        action={
          <span className="card__tools">
            {p.connected ? <Badge tone="success">Connected</Badge> : <Badge tone="warn">Not connected</Badge>}
            {p.connected && (p.livemode ? <Badge tone="success">Live</Badge> : <Badge tone="warn">Test mode</Badge>)}
          </span>
        }
        foot="Only Mivio HQ sees this. Every business connects its own Stripe account through it; the keys are sealed with HQ's own key and never shown again."
      >
        {p.connected && !editing ? (
          <>
            <dl className="dl payments-dl">
              <dt>Account</dt>
              <dd className="mono">{p.accountId}{p.name ? ` · ${p.name}` : ""}</dd>
              <dt>Secret key</dt>
              <dd className="mono">{p.hint}</dd>
              <dt>Businesses connected</dt>
              <dd>{p.connectedBusinesses}</dd>
              <dt>Checked</dt>
              <dd>{p.verifiedAt ? fmtDateTime(p.verifiedAt) : "—"}</dd>
            </dl>
            <div className="payments-fee">
              <NumberField label="Mivio's fee" value={fee} min={0} max={20} width={120} suffix="% of each payment" onChange={setFee} help="Taken from each payment as Stripe's application fee. 0 for none." />
              {Number(fee) !== p.feePercent && (
                <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => void saveFee()}>Save fee</button>
              )}
            </div>
            <div className="twostep-row payments-actions">
              <button type="button" className="btn" onClick={() => setEditing(true)}>Replace keys</button>
              <button type="button" className="btn btn--plain btn--danger-ghost" onClick={() => setOff(true)}>Disconnect</button>
            </div>
          </>
        ) : (
          <form onSubmit={(e) => void save(e)}>
            <ol className="payments-steps">
              <li>In Stripe, switch on Connect for Mivio's account (Settings › Connect), with businesses getting their own full Stripe accounts.</li>
              <li>
                In Stripe › Developers › Webhooks, add an endpoint that listens to events on <strong>connected accounts</strong>, at this address, with these events:
                <CopyRow label="Address" value={p.webhookUrl} />
                <p className="muted payments-events">{p.webhookEvents.join(", ")}</p>
              </li>
              <li>Paste the account's secret key (Developers › API keys) and the endpoint's signing secret below. Use test-mode keys first.</li>
            </ol>
            {problem && <Banner tone="critical" title="Not connected">{problem}</Banner>}
            <TextField label="Secret key" value={secretKey} onChange={setSecretKey} secret error={errors.secretKey} help="Starts with sk_test_ or sk_live_ (or rk_ for a restricted key)." />
            <TextField label="Webhook signing secret" value={webhookSecret} onChange={setWebhookSecret} secret error={errors.webhookSecret} help="Starts with whsec_." />
            <NumberField label="Mivio's fee" value={fee} min={0} max={20} width={120} suffix="%" onChange={setFee} error={errors.feePercent} help="0 for none. It can be changed later." />
            <div className="twostep-row">
              <button type="submit" className="btn btn--primary" disabled={busy || !secretKey.trim() || !webhookSecret.trim()}>{busy ? "Checking with Stripe…" : "Check and connect"}</button>
              {p.connected && <button type="button" className="btn btn--plain" onClick={() => setEditing(false)}>Cancel</button>}
            </div>
          </form>
        )}
      </Card>
      <ConfirmDialog
        open={off}
        title="Disconnect Mivio's Stripe account?"
        text="Every business stops taking payments online until it's connected again. Their own Stripe accounts aren't touched."
        confirmLabel="Disconnect"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          setBusy(true);
          void api("/payments/platform", { method: "DELETE" })
            .then(() => {
              toast("Disconnected");
              void qc.invalidateQueries({ queryKey: ["payments"] });
            }, (err) => toast(errorText(err), "error"))
            .finally(() => {
              setBusy(false);
              setOff(false);
            });
        }}
        onCancel={() => setOff(false)}
      />
    </>
  );
}
